"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • DOE Energy Saver — Refrigerator Maintenance: clean condenser coils
//     periodically; check door seals; keep set points at 35–38°F
//     (refrigerator) and 0°F (freezer); replace water filters per
//     manufacturer schedule.
//     https://www.energy.gov/energysaver/refrigerators
//   • ENERGY STAR refrigerator best practices: maintain proper temperature
//     and clean coils; condenser-coil cleaning is one of the highest-ROI
//     maintenance tasks affecting energy use.
//   • Whirlpool, GE, LG, Samsung, Frigidaire use-and-care guides:
//     6-month water filter replacement, periodic coil cleaning, monthly
//     drip-pan / drain-line check, monthly gasket cleaning.
//   • USDA FSIS — keep refrigerator at or below 40°F, freezer at or below
//     0°F.

const SECTIONS = [
  { id: "why", t: "Why a schedule" },
  { id: "monthly", t: "Monthly tasks" },
  { id: "quarterly", t: "Quarterly tasks" },
  { id: "biannual", t: "Twice-a-year tasks" },
  { id: "annual", t: "Annual tasks" },
] as const

export function MaintenanceScheduleGeneratorPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Maintenance Schedule"
      heroEyebrow="Maintenance"
      heroTitlePre="The maintenance schedule that"
      heroTitleItalic="actually"
      heroTitlePost="extends fridge life."
      heroSubtitle="DOE and ENERGY STAR identify the same handful of maintenance tasks that protect efficiency, food safety, and lifespan. Here they are on a single calendar — monthly, quarterly, semi-annually, and annually."
      heroMeta={["⏱ ~5 min read", "DOE + ENERGY STAR + manufacturer guides"]}
      heroShare={<ToolShareBar title="Refrigerator Maintenance Schedule" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The two anchor tasks
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Vacuum condenser coils every <em className="italic text-[#ff9951]">6 months</em>.
            Replace the water filter every{" "}
            <em className="italic text-[#ff9951]">6 months</em>.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Fridge target
              </div>
              <div className="text-[22px] font-black tracking-tight">35–38°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Freezer target
              </div>
              <div className="text-[22px] font-black tracking-tight">0°F</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Does cleaning the condenser coils actually save energy?",
          answer:
            "Yes — DOE Energy Saver and ENERGY STAR both call out coil cleaning as a meaningful efficiency task. A coil packed with dust forces the compressor to run longer to dissipate the same heat. The exact savings depend on baseline dirt level; the cleaner the starting point, the smaller the delta, but the task remains the highest-ROI 10-minute maintenance you can do.",
        },
        {
          question: "What temperature should the fridge be set to?",
          answer:
            "USDA: refrigerator at or below 40°F (37°F is a common target); freezer at or below 0°F. DOE Energy Saver suggests 35–38°F for the refrigerator and 0°F for the freezer as the efficiency-and-safety sweet spot. Confirm with an appliance thermometer — factory dials are notoriously imprecise.",
        },
        {
          question: "How often should I really replace the water filter?",
          answer:
            "Every 6 months or per the manufacturer's gallon-rated capacity (typically 200–300 gallons), whichever comes first. The flow-restriction warning light isn't a freshness gauge — it's a pressure-drop sensor. Replace on the calendar.",
        },
        {
          question: "Do I need to defrost a frost-free fridge?",
          answer:
            "No — frost-free models cycle the defrost heater on their own. If you see frost building up anyway, the cause is usually a failing defrost heater, defrost thermostat, or a door gasket that's leaking humid air. Manual-defrost units (chest freezers especially) need defrosting when ice exceeds about 1/4 inch.",
        },
        {
          question: "When should I just replace the unit instead of maintaining it?",
          answer:
            "Refrigerators average 13–17 years of useful life per industry data, with efficiency declining noticeably after year 10. If your unit is past 10 years and needs a major repair (sealed-system work, compressor, control board), replacement usually wins on lifecycle cost. See our repair-vs-replace tool.",
        },
      ]}
      relatedTools={[
        { title: "Door Seal Test", category: "Maintenance", description: "Verify the gasket every 6 months.", href: "/tools/refrigerator-door-seal-test", time: "~3 min" },
        { title: "Temperature Settings Guide", category: "Temperature", description: "USDA + DOE set points.", href: "/tools/refrigerator-temperature-settings-guide", time: "~5 min" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "What the unit costs to run.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Lifespan", category: "Specialty", description: "Years left on the unit.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Past the 10-year mark and starting to fail?",
        body: "If maintenance isn't bringing back performance, replacement usually wins. Browse side-by-side refrigerators built for the long haul.",
        buttonLabel: "Browse side-by-side refrigerators",
        buttonHref: "/side-by-side-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive planner"
              title="Get your maintenance plan"
              intro="Age, pets, and a water system change the intervals. Three questions, one plan."
              questions={[
                            {
                              id: "age",
                              label: "How old is the refrigerator?",
                              options: [
                                { value: "new", label: "Under 5 years" },
                                { value: "mid", label: "5\u201310 years" },
                                { value: "old", label: "10+ years" }
                              ],
                            },
                            {
                              id: "pets",
                              label: "Shedding pets in the house?",
                              options: [
                                { value: "yes", label: "Yes" },
                                { value: "no", label: "No" }
                              ],
                            },
                            {
                              id: "water",
                              label: "Ice maker or water dispenser?",
                              options: [
                                { value: "yes", label: "Yes" },
                                { value: "no", label: "No" }
                              ],
                            }
              ]}
              compute={(a) => {
                            const coils = a.pets === "yes" ? "every 3 months" : "every 6 months"
                            const filter = a.water === "yes" ? " Change the water filter every 6 months and sanitize the dispenser line when you do." : ""
                            const aged = a.age === "old" ? " At 10+ years, add a quarterly listen for compressor clicking/short-cycling and run the door-seal dollar-bill test every 3 months \u2014 gaskets and start relays are the common age failures." : a.age === "mid" ? " From year 5, check the door gasket at every coil cleaning \u2014 it is the highest-ROI part to catch early." : ""
                            return { tone: "good", verdict: "Coils " + coils + ", gasket every 6 months", detail: "Vacuum the condenser coils " + coils + " (shedding pets halve the interval), empty and wash the drip pan when you do, and run the dollar-bill gasket test twice a year." + filter + aged }
                          }}
              ctaLabel="Build my plan"
              footnote="Intervals: DOE residential maintenance guidance, detailed below."
            />
            <section id="why" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Why a real schedule beats &ldquo;when I notice something&rdquo;
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Refrigerators run continuously for 10–17 years on average. Most failures
                don&apos;t arrive as obvious breakdowns — they accumulate as gradual efficiency
                loss, gasket creep, and slow biofilm growth. Caught early, almost all are reversed
                by 15 minutes of routine work. Caught late, they end as compressor or sealed-system
                failures that justify replacement.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                The schedule below pulls together what DOE Energy Saver, ENERGY STAR, USDA, and the
                major manufacturer use-and-care guides agree on. Calendar reminders cost nothing.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Two anchors carry the calendar
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    If you only do two things: <strong>vacuum the condenser coils</strong> and{" "}
                    <strong>replace the water filter</strong> every 6 months. DOE and every major
                    manufacturer agree these two protect efficiency and water quality more than
                    anything else you can do at home.
                  </p>
                </div>
              </div>
            </section>

            <section id="monthly" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Monthly — 10 minutes
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Verify temperatures with an independent thermometer", d: "Place a thermometer in a glass of water in the fridge and one in the freezer. USDA target: ≤40°F (fridge) and 0°F (freezer). Adjust dials if drifted." },
                  { n: "02", t: "Wipe down the door gasket", d: "Warm soapy water, soft cloth. No bleach, no ammonia, no abrasives — they degrade rubber per manufacturer guides." },
                  { n: "03", t: "Check the drain pan and drain line", d: "Pull the kick plate, look for standing water or musty smell. Manufacturer guides recommend a quarterly flush; check monthly so you catch a clog before overflow." },
                  { n: "04", t: "Wipe interior spills", d: "Dried spills become hard biofilm and odors. Catch them while they're soft." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="quarterly" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Quarterly — 30 minutes
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Empty, clean, and reload the interior", d: "Pull everything out, wipe shelves and drawers with warm soapy water, dry, reload by category. Discard anything past use-by." },
                  { n: "02", t: "Flush the drain line", d: "A turkey baster of warm water with a teaspoon of baking soda flushed through the drain hole prevents the algae that causes leaks under the crisper." },
                  { n: "03", t: "Vacuum visible coil dust", d: "If your model's coils are easy to reach (back or under the kick plate), a quick vacuum between full cleanings helps." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="biannual" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Twice a year — 1 hour
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Vacuum the condenser coils thoroughly", d: "DOE Energy Saver lists this as a primary refrigerator maintenance task. Pull the unit out enough to access the back (or remove the kick plate for under-mount coils). Vacuum every visible fin." },
                  { n: "02", t: "Replace the water and ice-maker filter", d: "Every 6 months or 200–300 gallons per the manufacturer. Flush 2–3 gallons through the new filter before drinking." },
                  { n: "03", t: "Run the dollar-bill door-seal test", d: "Close a dollar bill in the door and pull. If it slides out without drag, the gasket is failing. Replace per the door-seal test guide." },
                  { n: "04", t: "Sanitize the ice bin", d: "Empty, wash with warm soapy water, sanitize with a manufacturer-approved cleaner, dry, reinstall. Throw out the first 2–3 batches the unit makes after." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="annual" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Annual — bigger checks
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Re-verify the cabinet is level and tilted slightly back",
                  "Inspect the water-supply line for kinks, drips, frost",
                  "Listen for new compressor or fan noises",
                  "Confirm doors auto-close from a 45° open position",
                  "Check defrost-cycle behavior — frost on freezer walls = problem",
                  "Take a photo of the model/serial label so you have it on file",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If you replace it"
          title="Side-by-side refrigerators to compare"
          description="Check where each model's condenser sits: one you can reach is easier to keep on the schedule above."
          collectionType="side-by-side"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
